/**
 * CREATIVE DETONATOR - Dashboard Sidebar Management
 * Handles sidebar collapse/expand, mobile menu, and component loading
 *
 * Extracted from dashboard/core.js for better modularity
 * @version 1.0
 */

import { onDocument } from '../../../js/core/events.js';
import { createLogger } from '../../../js/core/logger.js';
import { Sounds } from '../../../js/shared/sounds.js';

const log = createLogger('sidebar');

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  collapsedClass: 'collapsed',
  storageKey: 'cd_sidebar_collapsed'
};

// ============================================================================
// SIDEBAR MODULE
// ============================================================================

export const Sidebar = {
  element: null,
  collapseBtn: null,
  mobileMenuBtn: null,
  loaded: false,

  /**
   * Load sidebar HTML from component file
   */
  async loadComponent() {
    if (this.loaded) return true;

    this.element = document.getElementById('sidebar');
    if (!this.element) return false;

    // Skip if sidebar already has content (fallback for non-component pages)
    if (this.element.children.length > 0) {
      this.loaded = true;
      return true;
    }

    try {
      // Fetch component from dashboard folder
      const response = await fetch('/features/dashboard/components/sidebar.html');
      if (!response.ok) throw new Error(`Failed to load sidebar: ${response.status}`);

      const html = await response.text();
      this.element.innerHTML = html;
      this.loaded = true;

      log.debug('Sidebar component loaded successfully');
      return true;
    } catch (error) {
      log.warn('Sidebar: Failed to load component:', error.message);
      return false;
    }
  },

  /**
   * Initialize sidebar functionality
   */
  init() {
    this.element = document.getElementById('sidebar');
    this.collapseBtn = document.getElementById('btn-collapse');
    this.mobileMenuBtn = document.getElementById('btn-mobile-menu');
    this.closeBtn = document.getElementById('sidebar-close');

    if (!this.element) return;

    // Restore saved state
    const isCollapsed = localStorage.getItem(CONFIG.storageKey) === 'true';
    if (isCollapsed) {
      this.element.classList.add(CONFIG.collapsedClass);
    }

    // Bind events
    if (this.collapseBtn) {
      this.collapseBtn.addEventListener('click', () => this.toggle());
    }

    if (this.mobileMenuBtn) {
      this.mobileMenuBtn.addEventListener('click', () => this.toggleMobile());
      this.mobileMenuBtn.setAttribute('aria-expanded', 'false');
    }

    // Close button (mobile)
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeMobile());
    }

    // Close mobile menu on outside click (scoped listener)
    onDocument('click', (e) => {
      if (this.element.classList.contains('mobile-open')) {
        if (!this.element.contains(e.target) && !this.mobileMenuBtn?.contains(e.target)) {
          this.closeMobile();
        }
      }
    }, 'dashboard-sidebar');

    // Close on escape key
    onDocument('keydown', (e) => {
      if (e.key === 'Escape' && this.element.classList.contains('mobile-open')) {
        this.closeMobile();
      }
    }, 'dashboard-sidebar-escape');

    // Initialize logo press effect
    this.initLogoPressEffect();
  },

  /**
   * Initialize logo press effect (classic Mac button behavior)
   */
  initLogoPressEffect() {
    const logoIcon = this.element?.querySelector('.sidebar-logo-icon');
    if (logoIcon && logoIcon.dataset.normal && logoIcon.dataset.pressed) {
      logoIcon.addEventListener('mousedown', () => {
        logoIcon.src = logoIcon.dataset.pressed;
        logoIcon.classList.add('pressed');
      });
      logoIcon.addEventListener('mouseup', () => {
        logoIcon.src = logoIcon.dataset.normal;
        logoIcon.classList.remove('pressed');
      });
      logoIcon.addEventListener('mouseleave', () => {
        logoIcon.src = logoIcon.dataset.normal;
        logoIcon.classList.remove('pressed');
      });
      // Touch support
      logoIcon.addEventListener('touchstart', () => {
        logoIcon.src = logoIcon.dataset.pressed;
        logoIcon.classList.add('pressed');
      }, { passive: true });
      logoIcon.addEventListener('touchend', () => {
        logoIcon.src = logoIcon.dataset.normal;
        logoIcon.classList.remove('pressed');
      }, { passive: true });
    }
  },

  /**
   * Toggle sidebar collapsed state
   */
  toggle() {
    if (!this.element) return;

    this.element.classList.toggle(CONFIG.collapsedClass);
    const isCollapsed = this.element.classList.contains(CONFIG.collapsedClass);
    localStorage.setItem(CONFIG.storageKey, isCollapsed);

    Sounds.play('click');
  },

  /**
   * Toggle mobile menu open/close
   */
  toggleMobile() {
    if (!this.element) return;
    const isOpen = this.element.classList.toggle('mobile-open');
    this.element.classList.toggle('open', isOpen);
    document.body.classList.toggle('sidebar-mobile-open', isOpen);
    if (this.mobileMenuBtn) {
      this.mobileMenuBtn.setAttribute('aria-expanded', String(isOpen));
    }

    // Lock/unlock body scroll
    if (isOpen) {
      this.lockBodyScroll();
    } else {
      this.unlockBodyScroll();
    }

    Sounds.play('click');
  },

  /**
   * Close mobile menu
   */
  closeMobile() {
    if (!this.element) return;
    this.element.classList.remove('mobile-open');
    this.element.classList.remove('open');
    document.body.classList.remove('sidebar-mobile-open');
    if (this.mobileMenuBtn) {
      this.mobileMenuBtn.setAttribute('aria-expanded', 'false');
    }
    this.unlockBodyScroll();
  },

  /**
   * Lock body scroll (for mobile drawer)
   */
  lockBodyScroll() {
    // Store current scroll position
    this.scrollPosition = window.scrollY;
    document.body.style.setProperty('--scroll-top', `-${this.scrollPosition}px`);
  },

  /**
   * Unlock body scroll (restore position)
   */
  unlockBodyScroll() {
    document.body.style.removeProperty('--scroll-top');
    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
  },

  /**
   * Check if sidebar is collapsed
   * @returns {boolean}
   */
  isCollapsed() {
    return this.element?.classList.contains(CONFIG.collapsedClass) || false;
  },

  /**
   * Check if mobile menu is open
   * @returns {boolean}
   */
  isMobileOpen() {
    return this.element?.classList.contains('mobile-open') || false;
  }
};

/**
 * Update sidebar active state based on current page
 * Uses data-page attribute on body element with URL fallback
 */
export function updateSidebarActiveState() {
  // Get current page from body data attribute
  const currentPage = document.body.dataset.page;

  // Fallback: detect from URL if no data attribute
  const detectedPage = currentPage || detectPageFromUrl();

  if (!detectedPage) {
    log.warn('Sidebar: Could not determine current page');
    return;
  }

  // Remove all active states
  const navItems = document.querySelectorAll('.sidebar-nav .nav-item');
  navItems.forEach(item => item.classList.remove('active'));

  // Find and activate matching nav item by data-page attribute
  const activeItem = document.querySelector(
    `.sidebar-nav .nav-item[data-page="${detectedPage}"]`
  );

  if (activeItem) {
    activeItem.classList.add('active');
  }
}

/**
 * Detect page from URL (fallback for backwards compatibility)
 * @returns {string}
 */
function detectPageFromUrl() {
  const path = window.location.pathname;
  const filename = path.split('/').pop().replace('.html', '');
  return filename || 'index';
}
